import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { toast } from 'sonner';
import { Save, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API_URL = getApiUrl();

export default function BlogConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/admin/blog/config`);
      setConfig(data);
    } catch (err) {
      toast.error('Failed to load blog config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/blog/config`, config);
      setConfig(data);
      toast.success('Blog config saved');
    } catch (err) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  const updateTrimester = (index, field, value) => {
    const updated = [...(config.trimesters || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField('trimesters', updated);
  };

  const updateCategory = (index, field, value) => {
    const updated = [...(config.categories || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField('categories', updated);
  };

  const updateCta = (key, field, value) => {
    const updated = { ...(config.cta_copy || {}) };
    updated[key] = { ...updated[key], [field]: value };
    updateField('cta_copy', updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-20 text-slate-500" data-testid="blog-config-page">
        <p className="font-medium">Blog config not found.</p>
        <p className="text-sm mt-1 mb-4">This usually resolves on server restart. You can also seed sample data manually.</p>
        <Button onClick={async () => {
          try {
            await axios.post(`${API_URL}/api/admin/blog/seed`);
            toast.success('Blog data seeded');
            fetchConfig();
          } catch (err) { toast.error('Seed failed'); }
        }} data-testid="seed-blog-btn">
          Seed Blog Data
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl" data-testid="blog-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="blog-config-title">Blog Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Control the entire blog layout from one place</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchConfig} className="gap-2">
            <RotateCcw className="w-4 h-4" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-config-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-white rounded-lg border border-slate-200 p-6" data-testid="config-hero-section">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Hero Section</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Hero Stat</Label>
            <Input
              value={config.hero_stat || ''}
              onChange={(e) => updateField('hero_stat', e.target.value)}
              placeholder="85% natural deliveries"
              data-testid="hero-stat-input"
            />
          </div>
          <div>
            <Label>Hero Sub Stat</Label>
            <Input
              value={config.hero_sub_stat || ''}
              onChange={(e) => updateField('hero_sub_stat', e.target.value)}
              placeholder="7,500+ mothers guided"
              data-testid="hero-sub-stat-input"
            />
          </div>
        </div>
      </section>

      {/* Trimester Journey */}
      <section className="bg-white rounded-lg border border-slate-200 p-6" data-testid="config-trimesters-section">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Trimester Journey</h2>
        <div className="space-y-4">
          {(config.trimesters || []).map((tri, idx) => (
            <div key={tri.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-xs text-slate-500">Label</Label>
                <Input
                  value={tri.label}
                  onChange={(e) => updateTrimester(idx, 'label', e.target.value)}
                  data-testid={`trimester-label-${idx}`}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Weeks</Label>
                <Input
                  value={tri.weeks}
                  onChange={(e) => updateTrimester(idx, 'weeks', e.target.value)}
                  data-testid={`trimester-weeks-${idx}`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-slate-500">Tagline</Label>
                <Input
                  value={tri.tagline}
                  onChange={(e) => updateTrimester(idx, 'tagline', e.target.value)}
                  data-testid={`trimester-tagline-${idx}`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white rounded-lg border border-slate-200 p-6" data-testid="config-categories-section">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Category Labels</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(config.categories || []).map((cat, idx) => (
            <div key={cat.value} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-xs font-mono text-slate-400 w-32 shrink-0">{cat.value}</span>
              <Input
                value={cat.label}
                onChange={(e) => updateCategory(idx, 'label', e.target.value)}
                className="flex-1"
                data-testid={`category-label-${cat.value}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* CTA Copy */}
      <section className="bg-white rounded-lg border border-slate-200 p-6" data-testid="config-cta-section">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">CTA Copy (per category)</h2>
        <div className="space-y-3">
          {Object.entries(config.cta_copy || {}).map(([key, val]) => (
            <div key={key} className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs font-mono text-slate-400 mb-2">{key}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Heading</Label>
                  <Input
                    value={val?.heading || ''}
                    onChange={(e) => updateCta(key, 'heading', e.target.value)}
                    data-testid={`cta-heading-${key}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Sub text</Label>
                  <Input
                    value={val?.sub || ''}
                    onChange={(e) => updateCta(key, 'sub', e.target.value)}
                    data-testid={`cta-sub-${key}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-config-bottom-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
